import { useEffect, useMemo } from 'react';

import type { LanguageOption } from '../components/LanguageSelect/types';
import { BASE_AVAILABLE_LANGUAGES, resolveAvailableLanguages, setAvailableLanguages } from '../i18n/availableLanguages';
import type { SupportedLanguages } from '../i18n/constants';
import { useFlag } from '../services/launchDarkly';

/**
 * The languages this user may currently pick.
 *
 * render.tsx resolves the same value from the bootstrap payload before i18n starts; this hook is
 * the live view of it, so a targeting change that lands after the LD client connects is picked up
 * without a reload.
 */
export function useAvailableLanguages(): SupportedLanguages[] {
  const flagValue = useFlag<string[]>('available_languages', BASE_AVAILABLE_LANGUAGES);

  const availableLanguages = useMemo(() => resolveAvailableLanguages(flagValue), [flagValue]);

  useEffect(() => {
    setAvailableLanguages(availableLanguages);
  }, [availableLanguages]);

  return availableLanguages;
}

/**
 * Narrows a selector's option list to the languages available to this user.
 * Pass the list the call site would otherwise have rendered — country restrictions and any other
 * business rules stay where they are, and this only removes what the flags have not opened.
 */
export function useAvailableLanguageOptions(options: LanguageOption[]): LanguageOption[] {
  const availableLanguages = useAvailableLanguages();

  return useMemo(
    () => options.filter((option) => availableLanguages.includes(option.id as SupportedLanguages)),
    [options, availableLanguages],
  );
}
